import React from 'react'
import { configureAbly, useChannel } from '@ably-labs/react-hooks'

import {
    namedOperations,
    useCountMyUnreadUserNotificationsQuery,
    useMarkAllMyUserNotificationsAsReadMutation,
    useMarkMyUserNotificationsAsSeenMutation,
    useMarkUserNotificationAsReadMutation,
    useQueryMyUserNotificationsQuery,
    UserNotificationFieldsFragment,
    UserNotificationType,
} from '~graphql/generated/graphql'
import { useUserContext } from '~config/user-context'
import {
    ChannelTypeNames,
    constructChannel,
    getAblyMessageData,
} from '~utils/ably-helpers'
import { AUDIO_SOUNDS, playAudio } from '~utils/audio-helpers'
import {
    DESKTOP_NOTIFICATION_TAGS,
    showDesktopNotification,
} from '~utils/desktop-notifications'

const PAGE_SIZE = 25

export type UserNotificationsContextType = {
    notifications: UserNotificationFieldsFragment[]
    unreadCount: number
    /** Number of notifications the user has not yet seen in the bell tray. */
    unseenCount: number
    refresh: () => Promise<unknown>
    markAsRead: (notificationId: string) => Promise<unknown>
    markAllAsRead: () => Promise<unknown>
    /** Stamps `seen_at` on currently-unseen notifications - clears the badge. */
    markAllAsSeen: () => Promise<unknown>
}

const UserNotificationsContext =
    React.createContext<UserNotificationsContextType>({
        notifications: [],
        unreadCount: 0,
        unseenCount: 0,
        refresh: async () => {},
        markAsRead: async () => {},
        markAllAsRead: async () => {},
        markAllAsSeen: async () => {},
    })

export function useUserNotifications() {
    return React.useContext(UserNotificationsContext)
}

export default function UserNotificationsProvider({
    children,
}: React.PropsWithChildren) {
    return (
        <UserNotificationsContext.Provider value={useUserNotificationsValues()}>
            {children}
        </UserNotificationsContext.Provider>
    )
}

function useUserNotificationsValues(): UserNotificationsContextType {
    const { user } = useUserContext()

    const { data: listData, refetch: refetchList } =
        useQueryMyUserNotificationsQuery({
            variables: { page: 0, pageSize: PAGE_SIZE },
            skip: !user,
        })

    const { data: countData, refetch: refetchCount } =
        useCountMyUnreadUserNotificationsQuery({
            skip: !user,
        })

    const refetchAll = [
        namedOperations.Query.queryMyUserNotifications,
        namedOperations.Query.countMyUnreadUserNotifications,
    ]

    const [markUserNotificationAsRead] = useMarkUserNotificationAsReadMutation({
        refetchQueries: refetchAll,
    })
    const [markAllAsReadMutation] = useMarkAllMyUserNotificationsAsReadMutation(
        { refetchQueries: refetchAll },
    )
    const [markAllAsSeenMutation] = useMarkMyUserNotificationsAsSeenMutation({
        refetchQueries: refetchAll,
    })

    configureAbly({
        authUrl: `${process.env.GATSBY_REST_API_URL}/ably/auth`,
    })

    // Audio handling is split: team-chat mentions piggyback on
    // `UnreadInternalChatsProvider`, which already dings for every
    // team-chat message to the recipient (so playing here would
    // double-ding). Inbox internal-note mentions, on the other hand,
    // get NO audio coverage from `UnreadMessagesProvider` - that
    // provider only fires its sound for client-to-clinic messages
    // (see `handleNotifyClinic` -> `chatNotification-<clinicId>`),
    // never for staff-on-staff internal notes. So this provider is the
    // sole audio path for that case.
    useChannel(
        constructChannel(ChannelTypeNames.userNotification, user?.id || ''),
        message => {
            void Promise.all([refetchList(), refetchCount()])

            // Internal-note mentions are the one notification type that
            // has no other audio source (the existing inbox provider
            // only fires for client->clinic messages). They're also a
            // direct, high-signal cue, so we deliberately do NOT gate
            // them on `UserNotificationSound.Inbox`: a teammate who
            // muted general inbox dings still wants to hear when they
            // are personally addressed.
            //
            // Team-chat mentions are intentionally NOT played here -
            // `UnreadInternalChatsProvider` already dings for every
            // team-chat message to the recipient, so playing again
            // would double-ding.
            const type = getNotificationTypeFromAblyMessage(message)
            if (type === UserNotificationType.MentionedInInternalNote) {
                playAudio(AUDIO_SOUNDS.inbox)
                // Mirrors the sound logic: mentions bypass the sound toggles
                // because they are personal and high-signal.
                showDesktopNotification(
                    getMentionTitleFromAblyMessage(message) ??
                        'You were mentioned',
                    { tag: DESKTOP_NOTIFICATION_TAGS.mention },
                )
            }
        },
    )

    const notifications = listData?.queryMyUserNotifications || []
    const unreadCount = countData?.countMyUnreadUserNotifications || 0

    const unseenCount = notifications.filter(n => n.seen_at == null).length

    return {
        notifications,
        unreadCount,
        unseenCount,
        refresh: () => Promise.all([refetchList(), refetchCount()]),
        markAsRead: id =>
            markUserNotificationAsRead({ variables: { id } }),
        markAllAsRead: () => markAllAsReadMutation(),
        markAllAsSeen: () => markAllAsSeenMutation(),
    }
}

/**
 * The Ably payload from the server is `{ notificationId, type, title }`.
 * Pulls `type` off the payload and verifies it's a known
 * `UserNotificationType` before returning, otherwise null so the caller skips
 * audio/bubbles.
 */
function getNotificationTypeFromAblyMessage(
    message: unknown,
): UserNotificationType | null {
    const data = getAblyMessageData(message)
    if (typeof data !== 'object' || data === null || !('type' in data)) {
        return null
    }
    const { type } = data
    if (typeof type !== 'string') return null

    if (type in UserNotificationType) {
        return type as UserNotificationType
    }
    return null
}

/**
 * Same `{ notificationId, type, title }` payload - pulls `title` (e.g. "You
 * were mentioned in an Internal Note") for the bubble. Null when missing so
 * the caller falls back to generic copy.
 */
function getMentionTitleFromAblyMessage(message: unknown): string | null {
    const data = getAblyMessageData(message)
    if (typeof data !== 'object' || data === null || !('title' in data)) {
        return null
    }
    const { title } = data
    if (typeof title !== 'string') return null
    return title.trim() ? title : null
}
